<template>
  <vxe-table
    ref="xTable"
    empty-text="No data"
    border="none"
    :align="allAlign"
    :row-config="rowConfig"
    :cell-class-name="cellClassName"
    :data="routingStatusList"
    :sort-config="{ remote: true, defaultSort: defaultSort }"
    @sort-change="sortChangeEvent"
    @cell-click="clickCellEvent"
    class="routing-status-table"
  >
    <vxe-column
      type="html"
      field="delegatedPrefix"
      title="Prefix"
      sortable
      min-width="64px"
      width="24%"
      :formatter="formatPrefix"
    >
    </vxe-column>
    <vxe-column
      field="route.prefix"
      title="BGP route"
      sortable
      min-width="64px"
      width="24%"
    ></vxe-column>
    <vxe-column
      field="route.asn"
      title="Origin AS"
      sortable
      width="15%"
      :formatter="formatASN"
    ></vxe-column>
    <vxe-column
      type="html"
      field="roa_status"
      title="ROA"
      sortable
      width="17.5%"
      :formatter="formatRoaStatus"
    ></vxe-column>
    <vxe-column
      type="html"
      field="route_object_status"
      title="Route object"
      sortable
      width="19.5%"
      :formatter="formatRouteObjectStatus"
    ></vxe-column>
  </vxe-table>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import {
  ROA_STATUS,
  ROUTE_OBJECT_STATUS,
  MISMATCH_TYPE
} from '../../constants/routingStatusConstant';

export default {
  name: 'RoutingStatusTable',
  props: {
    routingStatusList: {
      type: Array,
      required: true
    },
    defaultSort: {
      type: Object,
      default: () => ({ field: '', order: '' })
    }
  },
  data() {
    return {
      allAlign: 'left', // 'left', 'right' or 'center'
      rowConfig: { isHover: false }
    };
  },
  computed: {
    ...mapState(['isExtraExtraLargeScreen'])
  },
  methods: {
    ...mapMutations('routingStatusStore', [
      'set_isShowMismatchModal',
      'set_mismatchModalData'
    ]),
    cellClassName({ row, rowIndex, column, columnIndex }) {
      if (column.property === 'delegatedPrefix') {
        if (row.aggregations) return 'prefix-with-aggregations';
      }

      if (column.property === 'roa_status') {
        let addedClassName = '';

        switch (row.roa_status) {
          case ROA_STATUS.PUBLISHED:
            addedClassName = 'prefix-icon prefix-icon--table green-dot';
            break;
          case ROA_STATUS.NOT_PUBLISHED:
            addedClassName = 'prefix-icon prefix-icon--table yellow-dot';
            break;
          case ROA_STATUS.MISMATCH:
            addedClassName = 'prefix-icon prefix-icon--table red-dot';
            break;
          default:
            addedClassName = '';
            break;
        }
        return addedClassName;
      }

      if (column.property === 'route_object_status') {
        let addedClassName = '';

        switch (row.route_object_status) {
          case ROUTE_OBJECT_STATUS.PUBLISHED:
            addedClassName = 'prefix-icon prefix-icon--table green-dot';
            break;
          case ROUTE_OBJECT_STATUS.NOT_PUBLISHED:
            addedClassName = 'prefix-icon prefix-icon--table yellow-dot';
            break;
          case ROUTE_OBJECT_STATUS.MISMATCH:
            addedClassName = 'prefix-icon prefix-icon--table red-dot';
            break;
          default:
            addedClassName = '';
            break;
        }
        return addedClassName;
      }
    },
    formatPrefix({ row, cellValue, rowIndex }) {
      let result = cellValue;

      if (!row.isPrefixHolder) result = `*${cellValue}`;

      if (row.aggregations) {
        let aggregationsEle = '';

        row.aggregations.forEach(
          item =>
            (aggregationsEle += `<div class='aggregation-item'>${item}</div>`)
        );

        if (this.isExtraExtraLargeScreen) {
          result = `<div id="index-${rowIndex}" class="aggregations"><div class="aggregations-toggle xxl-layout"><span class="prefix mr-1">${result}</span><div class="d-flex align-items-center"><span class="badge badge-primary mr-2">Aggregation</span><div class="icon-wrapper"><div class="button__arrow button__arrow--down"></div></div></div></div><div class="aggregation-list">${aggregationsEle}</div></div>`;
        } else {
          result = `<div id="index-${rowIndex}" class="aggregations"><div class="aggregations-toggle"><div class="prefix-wrapper"><span class="prefix">${result}</span><span class="badge badge-primary">Aggregation</span></div><div class="icon-wrapper"><div class="button__arrow button__arrow--down"></div></div></div><div class="aggregation-list">${aggregationsEle}</div></div>`;
        }
      }

      return result;
    },
    formatASN({ cellValue }) {
      return `AS${cellValue}`;
    },
    formatRoaStatus({ cellValue }) {
      let rowContent = '';

      switch (cellValue) {
        case ROA_STATUS.NOT_PUBLISHED:
          rowContent = `<span class="not-published">Not published</span>`;
          break;
        case ROA_STATUS.PUBLISHED:
          rowContent = `<span class="published">Published</span>`;
          break;
        case ROA_STATUS.MISMATCH:
          rowContent = `<span class="mismatch">Mismatch</span><span class="mismatch info">+ info</span>`;
          break;
        default:
          rowContent = `<span>${cellValue}</span>`;
      }

      return rowContent;
    },
    formatRouteObjectStatus({ row }) {
      const { route_object_status } = row;

      let rowContent = '';

      switch (route_object_status) {
        case ROUTE_OBJECT_STATUS.NOT_PUBLISHED:
          rowContent = `<span class="not-published">Not published</span>`;
          break;
        case ROUTE_OBJECT_STATUS.PUBLISHED:
          rowContent = `<span class="published">Published</span>`;
          break;
        case ROUTE_OBJECT_STATUS.MISMATCH:
          rowContent = `<span class="mismatch">Mismatch</span><span class="mismatch info">+ info</span>`;
          break;
        default:
          rowContent = `<span>${route_object_status}</span>`;
      }

      return rowContent;
    },

    sortChangeEvent({ property, order }) {
      this.$emit('onSetSort', { property, order });
    },

    clickCellEvent(event) {
      const { column, row, rowIndex } = event;

      if (column.field === 'delegatedPrefix' && row.aggregations) {
        const element = document.querySelector(`#index-${rowIndex}`);
        if (element) {
          if (element.classList.contains('expanded')) {
            element.classList.remove('expanded');
          } else {
            element.classList.add('expanded');
          }
        }
      }

      if (
        column.field === 'roa_status' &&
        row.roa_status === ROA_STATUS.MISMATCH
      ) {
        this.set_isShowMismatchModal(true);
        this.set_mismatchModalData({ type: MISMATCH_TYPE.ROA, data: row });
      }

      if (
        column.field === 'route_object_status' &&
        row.route_object_status === ROUTE_OBJECT_STATUS.MISMATCH
      ) {
        this.set_isShowMismatchModal(true);
        this.set_mismatchModalData({
          type: MISMATCH_TYPE.ROUTE_OBJECT,
          data: row
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.routing-status-table {
  /deep/ .vxe-header--row {
    background-color: #e6e6e6;
  }

  /deep/ .vxe-header--column {
    border-bottom: none;
  }

  /deep/ td.vxe-body--column {
    background-image: none;
    vertical-align: middle;
  }

  /deep/ .vxe-table--filter-wrapper {
    text-align: left;
  }

  /deep/ .status {
    padding-left: 0.5rem;
  }

  /deep/ .snooze {
    font-size: 0.8rem;
    font-style: italic;
  }

  /deep/ .prefix-icon {
    padding: 0.5rem 0 0.3rem 0;

    .vxe-cell--html {
      span {
        margin-left: 1rem;
      }
    }
  }

  /deep/ .published,
  /deep/ .not-published {
    display: inline-block;
    height: 2.75rem;
    line-height: 2.75rem;
  }

  /deep/ .mismatch {
    display: block;
    cursor: pointer;
  }

  /deep/ .info {
    display: block;
    position: relative;
    top: -0.2rem;
    width: 4.5rem;
    color: $text-light-blue;
  }

  /deep/ .prefix-with-aggregations {
    padding: 0.5rem 0 0.3rem 0;
  }

  /deep/ .aggregations {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    cursor: pointer;

    .button__arrow {
      background-color: transparent;
      height: 0.8rem;
      width: 0.8rem;
    }

    .button__arrow--down {
      border-bottom: 4px solid rgba(0, 0, 0, 0.5);
      border-right: 4px solid rgba(0, 0, 0, 0.5);
      transform: translateY(-25%) rotate(45deg);
      transition: all 0.3s;
    }

    .aggregations-toggle {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: center;
    }

    .aggregations-toggle.xxl-layout {
      flex-wrap: wrap;
    }

    .prefix-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      margin-right: 1rem;
    }
    .badge {
      border-radius: 0.25rem;
      font-weight: 600;
    }

    .aggregation-list {
      display: none;
    }
  }

  /deep/ .aggregations.expanded {
    .button__arrow--down {
      transform: translateY(25%) rotate(225deg);
    }

    .aggregation-list {
      display: flex;
      flex-direction: column;
      margin: 0.5rem 0 0 1rem;

      .aggregation-item {
        padding: 0.1rem 0;
        font-style: italic;
        font-size: 0.8rem;
      }
    }
  }
}
</style>
